import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffectEvent } from 'react';
import { AlertOctagon, ArrowUpDown, Check, ChevronDown, Filter, Search, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { type ExecutionControlSortMode } from '../components/ExecutionFiltersBar';
import { ExecutionGrid } from '../components/ExecutionGrid';
import { ExecutionCreateModal } from '../components/ExecutionCreateModal';
import { useExecutionTemplates } from '../hooks/useExecutionTemplates';
import { useExecutionList } from '../hooks/useExecutionList';
import { useExecutionDetails } from '../hooks/useExecutionDetails';
import { useExecutionMutations } from '../hooks/useExecutionMutations';
import { useExecutionRealtime } from '../hooks/useExecutionRealtime';
import { useMyTaskDetail } from '../../minha-mesa/hooks/useMyTaskDetail';
import { useMyTaskMutations } from '../../minha-mesa/hooks/useMyTaskMutations';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import type { ExecutionFiltersState } from '../types';
import type { ExecutionListItem, ExecutionTemplateSummary, ExecutionSortField, SortDirection } from '../../../services/executionService';
import type { WorkflowPriority, ProcessInstanceStatus } from '../../../types/workflow-v9';
import { useDrawerHost } from '../../../components/drawer/DrawerHostProvider';
import { PermissionKey } from '../../../types/workspace';
import { useAppToast } from '../../../components/AppToast';
import { useOperationalContext } from '../../../contexts/OperationalContext';
import { useExecutionModuleContext } from '../context/ExecutionModuleContext';
import {
  getExecutionRemainingSlaMinutes,
  getExecutionTotalSlaMinutes,
  getOperationalRiskLevel,
  getOperationalRiskRank,
} from '../../shared/utils/operationalRisk';

interface ExecutionDashboardProps {
  canCreateExecution: boolean;
  navigateOnSelect?: boolean;
  listItems: ExecutionListItem[];
  sortField: ExecutionSortField;
  sortDirection: SortDirection;
  activeSortLabel?: string;
  onSortChange: (field: ExecutionSortField, direction: SortDirection) => void;
  onSelectExecution: (id: string) => void;
  selectedExecutionId: string | null;
  listIsLoading: boolean;
  listIsFetching: boolean;
  listError: Error | null;
  onListRetry: () => void;
  onEmptyStateCta: () => void;
  onOpenDrawer: (id?: string | null) => void;
  templates?: ExecutionTemplateSummary[];
}

interface ExecutionDashboardPageProps {
  navigateOnSelect?: boolean;
}

export const ExecutionDashboardPage: React.FC<ExecutionDashboardPageProps> = ({
  navigateOnSelect = false,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { setRibbonState, clearRibbonState, setFiltersSlot } = useExecutionModuleContext();
  const userOrganizationId = user?.organization_id ?? user?.organizationId;
  const organizationId = workspace?.organizationId ?? userOrganizationId;
  const canCreateExecutionPermission = workspace?.permissions[PermissionKey.CAN_CREATE_EXECUTION] ?? false;
  const canPauseExecutionPermission = workspace?.permissions[PermissionKey.CAN_PAUSE_EXECUTION] ?? false;
  const canCancelExecutionPermission = workspace?.permissions[PermissionKey.CAN_CANCEL_EXECUTION] ?? false;
  const canReprocessExecutionPermission = workspace?.permissions[PermissionKey.CAN_REPROCESS_EXECUTION] ?? false;
  const userId = user?.id;
  const userName = user?.name;
  const executionIdParam = searchParams.get('executionId');
  const flowIdParam = searchParams.get('flowId');
  const flowNameParam = searchParams.get('flowName');
  const stageIdParam = searchParams.get('stageId');
  const stageNameParam = searchParams.get('stageName');
  const { state: drawerState, openDrawer, closeDrawer } = useDrawerHost();
  const { showToast, ToastContainer } = useAppToast();
  const {
    setActiveFlow,
    setActiveExecution,
    setActiveStage,
  } = useOperationalContext();

  const [filters, setFilters] = useState<ExecutionFiltersState>({
    status: ['active'],
    priority: [],
    search: '',
    overdue: false,
  });
  const [sortMode, setSortMode] = useState<ExecutionControlSortMode>('risk');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { templates, isLoading: templatesLoading, error: templatesError } = useExecutionTemplates(organizationId);

  useEffect(() => {
    if (templatesError) {
      showToast('error', `Erro ao carregar modelos corporativos: ${templatesError.message}`);
    }
  }, [templatesError, showToast]);

  const hasTemplatesAvailable = templates.length > 0;
  const createDisabledReason = useMemo(() => {
    if (!canCreateExecutionPermission) {
      return 'Permissão necessária: canCreateExecution';
    }
    if (templatesLoading) {
      return 'Sincronizando templates publicados...';
    }
    if (!hasTemplatesAvailable) {
      return 'Nenhum modelo disponível. Crie um blueprint publicado primeiro.';
    }
    return null;
  }, [canCreateExecutionPermission, templatesLoading, hasTemplatesAvailable]);

  const canCreateExecution = !createDisabledReason;

  const querySortField: ExecutionSortField = sortMode === 'risk' ? 'sla' : sortMode;
  const querySortDirection: SortDirection = sortMode === 'risk' ? 'asc' : sortDirection;

  const listQuery = useExecutionList({
    organizationId,
    filters,
    page: 1,
    pageSize: 1000,
    sortField: querySortField,
    sortDirection: querySortDirection,
  });
  const refetchExecutions = listQuery.refetch;

  const detailsQuery = useExecutionDetails(organizationId, selectedExecutionId ?? undefined);
  useExecutionRealtime(
    selectedExecutionId ?? undefined,
    organizationId,
    Boolean(organizationId && selectedExecutionId),
  );
  const mutations = useExecutionMutations({ organizationId });

  const selectedExecution = useMemo(() => {
    const fromPage = listQuery.items.find((item) => item.id === selectedExecutionId);
    return fromPage ?? detailsQuery.data?.instance ?? null;
  }, [listQuery.items, selectedExecutionId, detailsQuery.data]);
  const currentTaskSummary = useMemo(
    () =>
      detailsQuery.data?.tasks.find((task) => task.status === 'in_progress') ??
      detailsQuery.data?.tasks.find((task) => task.status === 'pending') ??
      detailsQuery.data?.tasks[0] ??
      null,
    [detailsQuery.data?.tasks],
  );
  const currentTaskId = currentTaskSummary?.id ?? null;
  const { task: currentTask } = useMyTaskDetail(currentTaskId);
  const handleTaskMutationSuccess = useCallback(() => {
    void detailsQuery.refetch();
    void refetchExecutions();
  }, [detailsQuery, refetchExecutions]);
  const taskMutations = useMyTaskMutations({
    organizationId,
    userId,
    userName,
    onSuccess: handleTaskMutationSuccess,
  });

  useEffect(() => {
    if (flowIdParam || flowNameParam) {
      setActiveFlow(flowIdParam, flowNameParam);
    }
    if (stageIdParam || stageNameParam) {
      setActiveStage(stageIdParam, stageNameParam);
    }
  }, [flowIdParam, flowNameParam, setActiveFlow, setActiveStage, stageIdParam, stageNameParam]);

  useEffect(() => {
    if (listQuery.items.length === 0) {
      setSelectedExecutionId(null);
      return;
    }
    if (executionIdParam && listQuery.items.some((item) => item.id === executionIdParam)) {
      setSelectedExecutionId(executionIdParam);
      return;
    }
    if (selectedExecutionId && listQuery.items.some((item) => item.id === selectedExecutionId)) {
      return;
    }
    setSelectedExecutionId(listQuery.items[0].id);
  }, [executionIdParam, listQuery.items, selectedExecutionId]);

  useEffect(() => {
    if (!selectedExecution) return;
    setActiveExecution(
      selectedExecution.id,
      selectedExecution.referenceCode ?? selectedExecution.title ?? selectedExecution.id,
      selectedExecution.templateId,
      selectedExecution.templateName,
    );
    if (selectedExecution.currentStageId || selectedExecution.currentStageName) {
      setActiveStage(selectedExecution.currentStageId ?? null, selectedExecution.currentStageName ?? null);
    }
  }, [selectedExecution, setActiveExecution, setActiveStage]);

  useEffect(() => {
    if (!selectedExecutionId && drawerState.module === 'execution') {
      closeDrawer();
    }
  }, [selectedExecutionId, drawerState.module, closeDrawer]);

  const handleFiltersChange = useCallback((changes: Partial<ExecutionFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleSortChange = useCallback((field: ExecutionControlSortMode, direction: SortDirection) => {
    setSortMode(field);
    setSortDirection(direction);
  }, []);

  const handleOpenDrawer = useCallback(
    (executionId?: string | null, mode?: string | null) => {
      if (!executionId) return;
      openDrawer({ module: 'execution', entityId: executionId, mode });
    },
    [openDrawer],
  );

  const handleOpenCreateModal = useCallback(() => {
    if (!canCreateExecution) return;
    setIsCreateModalOpen(true);
  }, [canCreateExecution]);

  useEffect(() => {
    if (searchParams.get('create') !== '1') {
      return;
    }
    if (canCreateExecution) {
      setIsCreateModalOpen(true);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  }, [canCreateExecution, searchParams, setSearchParams]);

  const handleCreateExecution = useCallback(
    async (payload: { templateId?: string; blueprintId?: string; versionId?: string; title: string; priority: WorkflowPriority }) => {
      const activeOrganizationId = organizationId;
      if (!activeOrganizationId) {
        throw new Error("organization_id não disponível");
      }
      console.debug('[ExecutionDashboard] createExecution organizationId', activeOrganizationId);
      try {
        const result = await mutations.createExecution.mutate({
          ...payload,
          initiatedBy: userId,
        });
        setIsCreateModalOpen(false);
        if (result?.id) {
          setSelectedExecutionId(result.id);
          navigate(`/execucoes/instancias/${encodeURIComponent(result.id)}`);
        }
        showToast('success', 'Execução criada com sucesso.');
        await refetchExecutions();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Não foi possível criar a execução.';
        showToast('error', message);
        throw new Error(message);
      }
    },
    [
      mutations.createExecution,
      userId,
      showToast,
      refetchExecutions,
      organizationId,
      navigate,
    ],
  );

  const handleEmptyStateCta = useCallback(() => {
    handleOpenCreateModal();
  }, [handleOpenCreateModal]);

  const handleListRetry = useCallback(() => {
    void refetchExecutions();
  }, [refetchExecutions]);

  //    Ribbon filter slot (portal dropdowns)
  const [openRibbonDropdown, setOpenRibbonDropdown] = useState<'status' | 'priority' | 'sort' | null>(null);
  const statusBtnRef   = useRef<HTMLButtonElement>(null);
  const priorityBtnRef = useRef<HTMLButtonElement>(null);
  const sortBtnRef     = useRef<HTMLButtonElement>(null);
  const [statusPos,   setStatusPos]   = useState({ top: 0, left: 0 });
  const [priorityPos, setPriorityPos] = useState({ top: 0, left: 0 });
  const [sortPos,     setSortPos]     = useState({ top: 0, left: 0 });

  const openDropdown = useCallback((which: 'status' | 'priority' | 'sort') => {
    const refs = { status: statusBtnRef, priority: priorityBtnRef, sort: sortBtnRef };
    const setPos = { status: setStatusPos, priority: setPriorityPos, sort: setSortPos };
    const r = refs[which].current?.getBoundingClientRect();
    if (r) setPos[which]({ top: r.bottom + 4, left: r.left });
    setOpenRibbonDropdown((prev) => (prev === which ? null : which));
  }, []);

  useEffect(() => {
    if (!openRibbonDropdown) return;
    const close = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-exec-dropdown="true"]') && !el.closest('button[data-exec-btn]')) {
        setOpenRibbonDropdown(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openRibbonDropdown]);

  const dropdownPanelClass =
    'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const dropdownItemClass =
    'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const STATUS_OPTIONS_LABEL = [
    { value: 'all',       label: 'Todos',      dot: '' },
    { value: 'active',    label: 'Ativos',     dot: '#3b82f6' },
    { value: 'on_hold',   label: 'Em Espera',  dot: '#94a3b8' },
    { value: 'completed', label: 'Concluídos', dot: '#22c55e' },
    { value: 'cancelled', label: 'Cancelados', dot: '#f87171' },
  ] as const;
  const PRIORITY_OPTIONS_LABEL = [
    { value: 'all',     label: 'Todas',   dot: '' },
    { value: 'critica', label: 'Crítica', dot: '#ef4444' },
    { value: 'alta',    label: 'Alta',    dot: '#f97316' },
    { value: 'media',   label: 'Média',   dot: '#3b82f6' },
    { value: 'baixa',   label: 'Baixa',   dot: '#94a3b8' },
  ] as const;
  const SORT_OPTIONS = [
    { value: 'risk'    as ExecutionControlSortMode, label: 'Risco' },
    { value: 'sla'     as ExecutionControlSortMode, label: 'SLA' },
    { value: 'elapsed' as ExecutionControlSortMode, label: 'Tempo' },
  ];
  const activeStatusVal   = filters.status[0]   ?? 'all';
  const activePriorityVal = filters.priority[0] ?? 'all';
  const activeStatusLabel   = STATUS_OPTIONS_LABEL.find((o) => o.value === activeStatusVal)?.label   ?? 'Todos';
  const activePriorityLabel = PRIORITY_OPTIONS_LABEL.find((o) => o.value === activePriorityVal)?.label ?? 'Todas';
  const activeSortLabel     = SORT_OPTIONS.find((o) => o.value === sortMode)?.label ?? 'Risco';

  const btnClass =
    'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const filtersSlot = useMemo(() => (
    <>
      {/* Status */}
      <button
        ref={statusBtnRef}
        data-exec-btn=""
        type="button"
        className={btnClass}
        onClick={() => openDropdown('status')}
      >
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openRibbonDropdown === 'status' && createPortal(
        <div
          data-exec-dropdown="true"
          className={dropdownPanelClass}
          style={{ position: 'fixed', top: statusPos.top, left: statusPos.left }}
        >
          {STATUS_OPTIONS_LABEL.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={dropdownItemClass}
              onClick={() => {
                handleFiltersChange({ status: opt.value === 'all' ? [] : [opt.value as ProcessInstanceStatus] });
                setOpenRibbonDropdown(null);
              }}
            >
              {opt.dot
                ? <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: opt.dot }} />
                : <span className="inline-block h-[7px] w-[7px] shrink-0" />}
              <span className="flex-1">{opt.label}</span>
              <Check size={11} className={activeStatusVal === opt.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Criticidade */}
      <button
        ref={priorityBtnRef}
        data-exec-btn=""
        type="button"
        className={btnClass}
        onClick={() => openDropdown('priority')}
      >
        <AlertOctagon size={12} className="text-[var(--text-secondary)]" />
        <span>{activePriorityLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openRibbonDropdown === 'priority' && createPortal(
        <div
          data-exec-dropdown="true"
          className={dropdownPanelClass}
          style={{ position: 'fixed', top: priorityPos.top, left: priorityPos.left }}
        >
          {PRIORITY_OPTIONS_LABEL.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={dropdownItemClass}
              onClick={() => {
                handleFiltersChange({ priority: opt.value === 'all' ? [] : [opt.value as WorkflowPriority] });
                setOpenRibbonDropdown(null);
              }}
            >
              {opt.dot
                ? <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: opt.dot }} />
                : <span className="inline-block h-[7px] w-[7px] shrink-0" />}
              <span className="flex-1">{opt.label}</span>
              <Check size={11} className={activePriorityVal === opt.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Ordem */}
      <button
        ref={sortBtnRef}
        data-exec-btn=""
        type="button"
        className={btnClass}
        onClick={() => openDropdown('sort')}
      >
        <ArrowUpDown size={12} className="text-[var(--text-secondary)]" />
        <span>{activeSortLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openRibbonDropdown === 'sort' && createPortal(
        <div
          data-exec-dropdown="true"
          className={dropdownPanelClass}
          style={{ position: 'fixed', top: sortPos.top, left: sortPos.left }}
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={dropdownItemClass}
              onClick={() => {
                handleSortChange(opt.value, sortDirection);
                setOpenRibbonDropdown(null);
              }}
            >
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{opt.label}</span>
              <Check size={11} className={sortMode === opt.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Busca */}
      <div className="flex w-[280px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            value={filters.search ?? ''}
            onChange={(e) => handleFiltersChange({ search: e.target.value })}
            placeholder="Buscar por código, template ou etapa..."
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {(filters.search ?? '') && (
            <button onClick={() => handleFiltersChange({ search: '' })} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </>
  ), [
    filters.search, filters.status, filters.priority, sortMode, sortDirection,
    openRibbonDropdown, statusPos, priorityPos, sortPos,
    handleFiltersChange, handleSortChange, openDropdown,
    activeStatusLabel, activePriorityLabel, activeSortLabel,
    activeStatusVal, activePriorityVal,
    btnClass, dropdownPanelClass, dropdownItemClass,
  ]);


  useEffect(() => {
    setFiltersSlot(filtersSlot);
  }, [filtersSlot, setFiltersSlot]);

  useEffect(() => () => setFiltersSlot(null), [setFiltersSlot]);
  const handleExportCsv = useCallback(() => {
    const items = listQuery.items;
    const headers = ['Cdigo', 'Ttulo', 'Status', 'Prioridade', 'Iniciado em', 'Concluído em', 'Vencido'];
    const rows = items.map((item) => [
      (item as { referenceCode?: string }).referenceCode ?? '',
      (item as { title?: string }).title ?? '',
      item.status ?? '',
      (item as { priority?: string }).priority ?? '',
      item.startedAt ? new Date(item.startedAt).toLocaleString('pt-BR') : '',
      (item as { completedAt?: Date | string }).completedAt
        ? new Date((item as { completedAt: Date | string }).completedAt).toLocaleString('pt-BR')
        : '',
      item.isOverdue ? 'Sim' : 'No',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `instancias_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [listQuery.items]);

  const handleExecutionAction = useEffectEvent(async (action: 'pause' | 'resume' | 'cancel') => {
    if (!selectedExecution) return;
    try {
      if (action === 'pause') {
        await mutations.pauseExecution.mutate(selectedExecution.id);
        showToast('info', 'Execucao pausada com sucesso.');
      }
      if (action === 'resume') {
        await mutations.resumeExecution.mutate(selectedExecution.id);
        showToast('success', 'Execucao retomada.');
      }
      if (action === 'cancel') {
        await mutations.cancelExecution.mutate({ executionId: selectedExecution.id });
        showToast('warning', 'Execucao cancelada.');
      }
      await detailsQuery.refetch();
      await refetchExecutions();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel executar esta acao.';
      showToast('error', message);
    }
  });

  const handleStartCurrentTask = useEffectEvent(async () => {
    if (!currentTaskSummary) return;
    await taskMutations.startTask(currentTaskSummary.id);
  });

  const handleCompleteCurrentTask = useEffectEvent(async () => {
    if (!currentTask || !selectedExecution || !userId) return;
    if (currentTask.status === 'pending') {
      await taskMutations.startTask(currentTask.id);
    }
    await taskMutations.completeTask({
      taskId: currentTask.id,
      instanceId: selectedExecution.id,
      result: currentTask.completionType === 'approve_reject' ? 'approved' : 'completed',
      notes: '',
      formData: {},
      checklistData: {},
      completedBy: userId,
      previousUpdatedAt: currentTask.updatedAt,
      blueprintVersionId: currentTask.metadata?.blueprint_version_id as string | undefined,
    });
  });

  const displayListItems = useMemo(() => {
    if (sortMode !== 'risk') {
      return listQuery.items;
    }

    const sortedItems = [...listQuery.items].sort((left, right) => {
      const leftRisk = getOperationalRiskLevel(
        getExecutionRemainingSlaMinutes(left),
        getExecutionTotalSlaMinutes(left),
      );
      const rightRisk = getOperationalRiskLevel(
        getExecutionRemainingSlaMinutes(right),
        getExecutionTotalSlaMinutes(right),
      );
      const riskDiff = getOperationalRiskRank(rightRisk) - getOperationalRiskRank(leftRisk);
      if (riskDiff !== 0) {
        return riskDiff;
      }

      const leftRemaining = getExecutionRemainingSlaMinutes(left);
      const rightRemaining = getExecutionRemainingSlaMinutes(right);
      if (leftRemaining === null && rightRemaining === null) {
        return 0;
      }
      if (leftRemaining === null) {
        return 1;
      }
      if (rightRemaining === null) {
        return -1;
      }
      return leftRemaining - rightRemaining;
    });

    return sortDirection === 'asc' ? [...sortedItems].reverse() : sortedItems;
  }, [listQuery.items, sortDirection, sortMode]);

  const instanceBusy =
    mutations.pauseExecution.isLoading ||
    mutations.resumeExecution.isLoading ||
    mutations.cancelExecution.isLoading ||
    mutations.reprocessExecution.isLoading;
  const canPauseOrResume = Boolean(
    selectedExecution &&
    canPauseExecutionPermission &&
    (selectedExecution.status === 'active' || selectedExecution.status === 'on_hold'),
  );
  const canCancelSelectedExecution = Boolean(
    selectedExecution &&
    canCancelExecutionPermission &&
    selectedExecution.status !== 'completed' &&
    selectedExecution.status !== 'cancelled',
  );
  const canReprocessSelectedExecution = Boolean(
    selectedExecution &&
    canReprocessExecutionPermission &&
    ['failed', 'cancelled', 'completed'].includes(selectedExecution.status),
  );
  const canStartCurrentTask = Boolean(currentTaskSummary && userId && currentTaskSummary.status === 'pending' && !taskMutations.isStarting);
  const canCompleteCurrentTask = Boolean(
    currentTask &&
    userId &&
    (currentTask.status === 'pending' || currentTask.status === 'in_progress') &&
    !taskMutations.isCompleting,
  );
  const selectedExecutionIdForRibbon = selectedExecution?.id ?? null;
  const selectedExecutionStatus = selectedExecution?.status ?? null;
  const canOpenStageAction = Boolean(selectedExecutionIdForRibbon);

  const handlePauseOrResumeSelectedExecution = useEffectEvent(() => {
    void handleExecutionAction(selectedExecutionStatus === 'on_hold' ? 'resume' : 'pause');
  });

  const handleCancelSelectedExecution = useEffectEvent(() => {
    void handleExecutionAction('cancel');
  });

  const handleReprocessSelectedExecution = useEffectEvent(() => {
    handleOpenDrawer(selectedExecutionIdForRibbon, 'reprocess');
  });

  const handleOpenSelectedExecutionActions = useEffectEvent(() => {
    handleOpenDrawer(selectedExecutionIdForRibbon, 'actions');
  });

  const executionRibbonState = useMemo(
    () => ({
      create: {
        enabled: canCreateExecution,
        onClick: handleOpenCreateModal,
        tooltip: createDisabledReason ?? undefined,
      },
      instance: {
        pauseLabel: selectedExecutionStatus === 'on_hold' ? 'Retomar' : 'Pausar',
        onPauseOrResume: handlePauseOrResumeSelectedExecution,
        canPauseOrResume,
        onCancel: handleCancelSelectedExecution,
        canCancel: canCancelSelectedExecution,
        onReprocess: handleReprocessSelectedExecution,
        canReprocess: canReprocessSelectedExecution,
        busy: instanceBusy,
      },
      stage: {
        onStart: handleStartCurrentTask,
        canStart: canStartCurrentTask,
        onComplete: handleCompleteCurrentTask,
        canComplete: canCompleteCurrentTask,
        onBlock: undefined,
        canBlock: false,
        onOpenAction: handleOpenSelectedExecutionActions,
        canOpenAction: canOpenStageAction,
      },
      refresh: {
        onRefresh: refetchExecutions,
        isRefreshing: listQuery.isFetching,
      },
      export: {
        onExport: handleExportCsv,
        canExport: listQuery.items.length > 0,
      },
    }),
    [
      canCancelSelectedExecution,
      canCompleteCurrentTask,
      canCreateExecution,
      canPauseOrResume,
      canReprocessSelectedExecution,
      canStartCurrentTask,
      createDisabledReason,
      handleOpenCreateModal,
      instanceBusy,
      canOpenStageAction,
      selectedExecutionStatus,
      refetchExecutions,
      listQuery.isFetching,
      listQuery.items.length,
      handleExportCsv,
    ],
  );

  useEffect(() => {
    setRibbonState(executionRibbonState);
  }, [executionRibbonState, setRibbonState]);

  useEffect(() => () => clearRibbonState(), [clearRibbonState]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-transparent">
      <div className="flex min-h-0 flex-1 flex-col">
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-full min-h-0 w-full flex-col">
            <ExecutionDashboard
              canCreateExecution={canCreateExecution}
              listItems={displayListItems}
              sortField={querySortField}
              sortDirection={sortDirection}
              activeSortLabel={sortMode === 'risk' ? 'RISCO' : undefined}
              onSortChange={(field, direction) => handleSortChange(field, direction)}
              onSelectExecution={(id) => {
                if (navigateOnSelect) {
                  navigate(`/execucoes/instancias/${encodeURIComponent(id)}`);
                  return;
                }
                const execution = listQuery.items.find((item) => item.id === id);
                setSelectedExecutionId(id);
                if (execution) {
                  setActiveExecution(
                    execution.id,
                    execution.referenceCode ?? execution.title ?? execution.id,
                    execution.templateId,
                    execution.templateName,
                  );
                  setActiveStage(execution.currentStageId ?? null, execution.currentStageName ?? null);
                }
                handleOpenDrawer(id);
              }}
              selectedExecutionId={selectedExecutionId}
              listIsLoading={listQuery.isLoading}
              listIsFetching={listQuery.isFetching}
              listError={listQuery.error}
              onListRetry={handleListRetry}
              onEmptyStateCta={handleEmptyStateCta}
              onOpenDrawer={handleOpenDrawer}
              navigateOnSelect={navigateOnSelect}
              templates={templates}
            />
          </div>
        </main>
      </div>

      <ExecutionCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        templates={templates}
        onSubmit={handleCreateExecution}
        isSubmitting={mutations.createExecution.isLoading}
      />
      <ToastContainer />
    </div>
  );
};

export const ExecutionDashboard: React.FC<ExecutionDashboardProps> = ({
  canCreateExecution,
  navigateOnSelect = false,
  listItems,
  sortField,
  sortDirection,
  activeSortLabel,
  onSortChange,
  onSelectExecution,
  selectedExecutionId,
  listIsLoading,
  listIsFetching,
  listError,
  onListRetry,
  onEmptyStateCta,
  onOpenDrawer,
  templates,
}) => {
  return (
    <ExecutionGrid
      items={listItems}
      sortField={sortField}
      sortDirection={sortDirection}
      activeSortLabel={activeSortLabel}
      onSortChange={onSortChange}
      onRowSelect={onSelectExecution}
      onRowOpen={navigateOnSelect ? undefined : onOpenDrawer}
      selectedId={navigateOnSelect ? null : selectedExecutionId}
      isLoading={listIsLoading}
      isFetching={listIsFetching}
      error={listError}
      onRetry={onListRetry}
      onEmptyCta={onEmptyStateCta}
      canCreateExecution={canCreateExecution}
      templates={templates}
    />
  );
};

